import { Box, CardContent, Divider, Stack } from '@mui/material';
import { DynamoDbBillingMode, Provider } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { BrowserGrid } from '@/components/shared/browserGrid';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { DirectRestoreWriteModeSelector } from './directRestoreWriteModeSelector';
import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreKeyspacesState } from '../restoreKeyspacesWizard';

const keyspaceNameConfig = getCloudFieldConfig(
  Provider.Aws,
  'keyspacesKeyspaceName',
  { required: true }
);
const tableNameConfig = getCloudFieldConfig(
  Provider.Aws,
  'keyspacesTableName',
  { required: true }
);

export const KeyspacesConfiguration = (
  props: StepProps<RestoreKeyspacesState>
) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  const canGoNext = () => {
    if (
      isRestoring ||
      !props.wizardState.regionName ||
      !props.wizardState.keyspaceName ||
      !props.wizardState.tableName ||
      !keyspaceNameConfig.validation.validateInput(
        props.wizardState.keyspaceName
      ) ||
      !tableNameConfig.validation.validateInput(props.wizardState.tableName) ||
      (props.wizardState.encryption?.mode === 'arn' &&
        !getEncryptionKey(props.wizardState.encryption)) ||
      (props.wizardState.writeMode === 'Provisioned' &&
        props.wizardState.writeCapacityUnits <= 0)
    ) {
      return false;
    }

    return true;
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };

    const restoreBillingMode =
      props.wizardState.writeMode === 'Provisioned'
        ? DynamoDbBillingMode.DYNAMODB_BILLING_MODE_PROVISIONED
        : DynamoDbBillingMode.DYNAMODB_BILLING_MODE_PAY_PER_REQUEST;

    return dal.restore
      .keyspacesTable(
        props.wizardState.resourceId,
        props.wizardState.snapshot.id,
        {
          restoreAccountId: props.wizardState.restoreAccount!.id,
          destination: {
            awsKeyspaces: {
              tags,
              encryptionKeyId:
                getEncryptionKey(props.wizardState.encryption) || '',
              keyspaceName: props.wizardState.keyspaceName || '',
              tableName: props.wizardState.tableName || '',
              region: props.wizardState.regionName || '',
              writeCapacityUnits: props.wizardState.writeCapacityUnits,
              restoreBillingMode,
            },
          },
        }
      )
      .then(() =>
        props.finish(
          `Started restore job for ${props.wizardState.keyspaceName}.${props.wizardState.tableName}`
        )
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setError(`Failed to restore Keyspaces table: ${errorMessage}`);
      })
      .finally(() => {
        setIsRestoring(false);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <BrowserGrid columns={2} gap='36px'>
          <FormTextField
            testId='keyspaceNameTextField'
            title='Keyspace name'
            validation={keyspaceNameConfig.validation}
            placeholder={keyspaceNameConfig.placeholder}
            value={props.wizardState.keyspaceName || ''}
            onChange={(event) =>
              props.setWizardState((state) => ({
                ...state,
                keyspaceName: event.target.value,
              }))
            }
          />
          <FormTextField
            testId='tableNameTextField'
            title='Table name'
            validation={tableNameConfig.validation}
            placeholder={tableNameConfig.placeholder}
            value={props.wizardState.tableName || ''}
            onChange={(event) =>
              props.setWizardState((state) => ({
                ...state,
                tableName: event.target.value,
              }))
            }
          />
          <RestoreRegionSelect
            isRequired
            cloudProvider={Provider.Aws}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(region) =>
              props.setWizardState((state) => ({
                ...state,
                regionName: region,
              }))
            }
          />
          <DirectRestoreWriteModeSelector
            writeMode={props.wizardState.writeMode}
            setWriteMode={(value) =>
              props.setWizardState((state) => ({
                ...state,
                writeMode: value,
                writeCapacityUnits: value === 'On-demand' ? 0 : 40_000,
              }))
            }
            writeCapacityUnits={props.wizardState.writeCapacityUnits}
            setWriteCapacityUnits={(value) =>
              props.setWizardState((state) => ({
                ...state,
                writeCapacityUnits: value,
              }))
            }
          />
        </BrowserGrid>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        {props.wizardState.regionName && (
          <EncryptionComponent
            encryption={props.wizardState.encryption}
            accountId={props.wizardState.restoreAccount!.id}
            regionName={props.wizardState.regionName}
            defaultOptionLabel='AWS owned key'
            onChange={(encryption) =>
              props.setWizardState((state) => ({
                ...state,
                encryption,
              }))
            }
          />
        )}
      </CardContent>
      <CardContent className='px-0 pb-[24px]'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
